<script setup lang="ts">
const props = defineProps<{ src: string; kind: 'audio' | 'video'; label: string; naturalSize?: boolean; autoPlay?: boolean }>()
const { t } = useI18n()
const media = ref<HTMLMediaElement | null>(null)
const playing = ref(false)
const failed = ref(false)
const muted = ref(false)
let generation = 0
async function toggle() {
  const element = media.value
  if (!element) return
  generation++
  if (!element.paused) { element.pause(); return }
  const request = generation
  failed.value = false
  try { await element.play() }
  catch { if (request === generation) failed.value = true }
}
async function startAutomaticPlayback(element: HTMLMediaElement, request: number) {
  const isCurrent = () => request === generation && props.autoPlay && media.value === element
  try { await element.play() }
  catch (error) {
    if (!isCurrent()) return
    if (!(error instanceof Error) || error.name !== 'NotAllowedError') { failed.value = true; return }
    element.muted = true
    muted.value = true
    try { await element.play() }
    catch (retryError) {
      if (isCurrent() && (!(retryError instanceof Error) || retryError.name !== 'NotAllowedError')) failed.value = true
    }
  }
  if (!props.autoPlay || media.value !== element) element.pause()
}
watch(() => [props.src, props.autoPlay, media.value] as const, ([, autoPlay, element], previous) => {
  const request = ++generation
  previous?.[2]?.pause()
  playing.value = false
  failed.value = false
  muted.value = false
  if (!element) return
  element.pause()
  element.muted = false
  if (autoPlay && props.kind === 'video') {
    element.currentTime = 0
    void startAutomaticPlayback(element, request)
  }
}, { flush: 'post', immediate: true })
function toggleSound() {
  if (!media.value) return
  muted.value = !media.value.muted
  media.value.muted = muted.value
}
onBeforeUnmount(() => { generation++; media.value?.pause() })
</script>

<template>
  <div class="event-media-player" :class="{ 'event-media-player--video': kind === 'video', 'event-media-player--natural-size': kind === 'video' && naturalSize }">
    <component :is="kind" :key="src" ref="media" :src="src" :preload="naturalSize && kind === 'video' ? 'metadata' : 'none'" playsinline
      controlslist="nodownload noplaybackrate noremoteplayback" disablepictureinpicture disableremoteplayback
      :aria-label="label" @contextmenu.prevent @play="playing = true" @pause="playing = false" @ended="playing = false" @volumechange="muted = Boolean(media?.muted)" />
    <TqButton compact variant="secondary" :icon="playing ? 'i-lucide-pause' : 'i-lucide-play'"
      :aria-label="`${t(`public.purchase.eventHelp.${playing ? 'pause' : 'play'}`)}: ${label}`" @click="toggle">
      <span class="event-media-player__label">{{ t(`public.purchase.eventHelp.${playing ? 'pause' : 'play'}`) }}</span>
    </TqButton>
    <TqButton v-if="kind === 'video' && autoPlay" compact variant="secondary" :icon="muted ? 'i-lucide-volume-x' : 'i-lucide-volume-2'" :aria-label="`${t(`public.purchase.eventHelp.${muted ? 'unmute' : 'mute'}`)}: ${label}`" @click="toggleSound">
      <span class="event-media-player__label">{{ t(`public.purchase.eventHelp.${muted ? 'unmute' : 'mute'}`) }}</span>
    </TqButton>
    <p v-if="failed" role="alert">{{ t('public.purchase.eventHelp.mediaError') }}</p>
  </div>
</template>

<style scoped>
.event-media-player { display: flex; flex-wrap: wrap; min-width: 0; max-width: 100%; align-items: center; gap: .5rem; }
.event-media-player--video { flex-direction: column; }
.event-media-player video { display: block; width: 100%; max-height: 24rem; object-fit: contain; }
.event-media-player--natural-size video { width: 100%; max-width: 100%; height: auto; max-height: none; }
.event-media-player audio { display: none; }
.event-media-player p { margin: 0; font-size: .85rem; color: var(--tq-text-soft); }
</style>
